import { useEffect, useRef } from 'react';

/**
 * Custom hook for PersonalisationModal logic and className selection.
 * @param {Object} options - Options object.
 * @param {boolean} options.isOpen - Whether the modal is open.
 * @param {function} options.onClose - Callback for closing the modal.
 * @returns {Object} View model for the modal component.
 */
export function useViewModel({ isOpen, onClose }) {
  // Handle personalisation modal dialog
  /** @type {React.RefObject<HTMLDialogElement | null>} */
  const dialogModalRef = useRef(null);

  // Handle content scroll position
  /** @type {React.RefObject<HTMLDivElement | null>} */
  const contentRef = useRef(null);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      dialog.showModal();
      setTimeout(() => {
        dialog.setAttribute('data-open', 'true');
      }, 10);
    } else if (!isOpen && dialog.open) {
      dialog.setAttribute('data-open', 'false');
      setTimeout(() => {
        dialog.close();
      }, 400);
    }
  }, [isOpen]);

  useEffect(() => {
    const dialog = dialogModalRef.current;
    if (!dialog) return;

    const handleNativeClose = () => {
      if (!isOpen) return;
      if (isOpen) {
        dialog.setAttribute('data-open', 'false');
        setTimeout(() => {
          onClose?.();
        }, 400);
      }
    };

    const handleBackdropClick = (e) => {
      if (e.target === dialog) {
        dialog.setAttribute('data-open', 'false');
        setTimeout(() => {
          onClose?.();
        }, 400);
      }
    };

    const handleCancel = (e) => {
      // Prevent default to manage our own closing animation, then close.
      e.preventDefault();
      dialog.setAttribute('data-open', 'false');
      setTimeout(() => {
        onClose?.();
      }, 400);
    };

    dialog.addEventListener('close', handleNativeClose);
    dialog.addEventListener('click', handleBackdropClick);
    dialog.addEventListener('cancel', handleCancel);
    return () => {
      dialog.removeEventListener('close', handleNativeClose);
      dialog.removeEventListener('click', handleBackdropClick);
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [isOpen, onClose]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  return {
    dialogModalRef,
    contentRef,
    handleClose
  };
}
